import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
} from 'react';
import { STORAGE_KEYS } from '../constants.js';

const GeneralContext = createContext();

export const GeneralContextProvider = ({ children, onCharIndexChanged }) => {
  const [charIndex, setCharIndex] = useState(
    JSON.parse(localStorage.getItem(STORAGE_KEYS.CHARACTER_INDEXES)) ?? []
  );
  const [rules, setRules] = useState(null);
  const failRollRefs = useRef([]);

  const parseSection = (sectionElement, parentPath = '') => {
    const title =
      sectionElement.getElementsByTagName('title')[0]?.textContent || '';
    const summary =
      sectionElement.getElementsByTagName('summary')[0]?.textContent;
    const description =
      sectionElement.getElementsByTagName('description')[0]?.textContent;
    const id = `${parentPath}-${title.replace(/\s+/g, '-').toLowerCase()}`;
    const childSections = Array.from(sectionElement.children)
      .filter(child => child.tagName.toLowerCase() === 'section')
      .map(child => parseSection(child, id));
    return { id, title, summary, description, subsections: childSections };
  };

  const fetchRules = () => {
    if (rules) return;

    fetch('/Rules_Source.xml')
      .then(res => res.text())
      .then(xmlString => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(
          xmlString.trimStart(),
          'text/xml'
        );
        const rootSections = Array.from(xmlDoc.getElementsByTagName('section'))
          .filter(s => s.parentElement.tagName.toLowerCase() === 'doc')
          .map(section => parseSection(section));

        setRules(rootSections);
      });
  };

  const registerFailRoll = useCallback(fn => {
    failRollRefs.current.push(fn);
  }, []);

  const triggerFailRoll = color => {
    if (!color) color = 'red';
    failRollRefs.current.forEach(fn => fn?.(color));
    onCharIndexChanged(true);
  };

  const unregisterFailRoll = useCallback(fn => {
    failRollRefs.current = failRollRefs.current.filter(f => f !== fn);
  }, []);

  const updateCharIndex = newValue => {
    setCharIndex(newValue);
    onCharIndexChanged(true);
  };

  const deleteCharacter = name => {
    if (name) {
      const idName = `${STORAGE_KEYS.CHARACTER_PREFIX}${name}`;
      if (charIndex.includes(idName)) {
        const updated = charIndex.filter(item => item !== idName);
        localStorage.setItem(
          STORAGE_KEYS.CHARACTER_INDEXES,
          JSON.stringify(updated)
        );
        updateCharIndex(updated);
        localStorage.removeItem(idName);
        return updated;
      }
    }
    return charIndex;
  };

  return (
    <GeneralContext.Provider
      value={{
        charIndex,
        rules,
        deleteCharacter,
        fetchRules,
        registerFailRoll,
        triggerFailRoll,
        unregisterFailRoll,
        updateCharIndex,
      }}
    >
      {children}
    </GeneralContext.Provider>
  );
};

export const useGeneralContext = () => useContext(GeneralContext);
